import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useRef } from "react";
import styled from "styled-components";

import { useBulkReprocessClaimMutation } from "../../store/queries/claims";
import { IClaim } from "../../types/claim";

interface IBulkReprocessModalProps {
  visible: boolean;
  onHide: () => void;
  selectedClaims: IClaim[];
  onSuccess: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

const ProgressBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
`;

/**
 * CLE-2172: confirm-and-apply modal for bulk reprocess.
 *
 * Same shape as the bulk payer modal minus the payer selector. Reprocess
 * re-parses each claim from its original EDI through the full pricing pipeline,
 * so it can take tens of seconds for a full batch.
 */
export function BulkReprocessModal({
  visible,
  onHide,
  selectedClaims,
  onSuccess,
}: IBulkReprocessModalProps) {
  const toastRef = useRef<Toast>(null);
  const [bulkReprocess, { isLoading: isApplying }] =
    useBulkReprocessClaimMutation();

  const handleApply = async () => {
    try {
      const result = await bulkReprocess({
        claimIds: selectedClaims.map((c) => c.id),
      }).unwrap();

      const detail =
        result.failureCount === 0
          ? `${result.successCount} claim${
              result.successCount === 1 ? "" : "s"
            } reprocessed.`
          : `${result.successCount} reprocessed, ${result.failureCount} failed.`;

      toastRef.current?.show({
        severity: result.failureCount === 0 ? "success" : "warn",
        summary: "Bulk reprocess",
        detail,
        life: 6000,
      });

      onSuccess();
    } catch (err: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Bulk reprocess failed",
        detail:
          err?.data?.error || err?.data?.messages?.[0] || "Request failed.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isApplying}
      />
      <Button
        label={`Reprocess ${selectedClaims.length}`}
        icon="pi pi-refresh"
        onClick={handleApply}
        disabled={isApplying}
        loading={isApplying}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Reprocess claims"
        visible={visible}
        onHide={isApplying ? () => {} : onHide}
        closable={!isApplying}
        footer={footer}
      >
        <Body>
          <div>
            Reprocess <strong>{selectedClaims.length}</strong> selected claim
            {selectedClaims.length === 1 ? "" : "s"} from the original EDI. The
            current matched payer is preserved; pricing rules, fee schedules,
            and code mappings are re-evaluated against the latest configuration.
          </div>

          {isApplying && (
            <ProgressBlock>
              <ProgressSpinner
                style={{ width: "24px", height: "24px" }}
                strokeWidth="4"
              />
              <span>
                Reprocessing {selectedClaims.length} claim
                {selectedClaims.length === 1 ? "" : "s"}. This may take a few
                minutes…
              </span>
            </ProgressBlock>
          )}

          {!isApplying && (
            <Hint>
              Each claim re-runs its full pricing pipeline from source.
            </Hint>
          )}
        </Body>
      </Dialog>
    </>
  );
}
